<script setup lang="ts">
  import type { Editor } from '@tiptap/vue-3'
  import type { ColorPickerMode } from 'naive-ui/es/color-picker/src/utils'
  import type { Ref } from 'vue'

  import { useRichEditorToolbar } from '@/shared/lib'

  const props = defineProps<{
    contentCounterLabel: string
    editor: Editor
    isCounterOverLimit: boolean
    toolbarAriaLabelKey: string
  }>()

  const emit = defineEmits<{
    (e: 'update:colorPickerOpen', value: boolean): void
  }>()

  // TipTap обновляет состояние без смены ссылки на Editor — форсируем пересчёт toolbar
  const editorRef = shallowRef(props.editor) as Ref<Editor | undefined>

  watch(
    () => props.editor,
    (editor, _, onCleanup) => {
      editorRef.value = editor

      const handleTransaction = () => {
        triggerRef(editorRef)
      }

      editor.on('transaction', handleTransaction)

      onCleanup(() => {
        editor.off('transaction', handleTransaction)
      })
    },
    { immediate: true },
  )

  const {
    alignActions,
    currentFontSize,
    fontSizeOptions,
    formatActions,
    handleHeadingSelect,
    headingOptions,
    historyActions,
    isFontSizeActive,
    isHeadingActive,
    isTextColorActive,
    setColor,
    setFontSize,
    textColor,
  } = useRichEditorToolbar(editorRef)

  const toolbarButtonClass =
    'hover:bg-secondary-bg text-body flex size-6 cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40'

  const toolbarButtonActiveClass = 'bg-secondary-bg ring-body ring-1 ring-inset'

  const getToolbarButtonClass = (isActive: boolean) => [toolbarButtonClass, isActive && toolbarButtonActiveClass]

  const fontSizeButtonClass =
    'hover:bg-secondary-bg text-body flex h-6 min-w-10 cursor-pointer items-center justify-center rounded border-none bg-transparent px-1.5 text-xs transition-colors'

  const handleFontSizeUpdate = (value: string | number | null) => {
    if (typeof value === 'string') {
      setFontSize(value)
    }
  }

  const handleColorUpdate = (value: string) => {
    setColor(value)
  }

  const handleColorPickerShowUpdate = (show: boolean) => {
    emit('update:colorPickerOpen', show)

    if (show) {
      props.editor.commands.blur()
    }
  }

  const COLOR_PICKER_MODES: ColorPickerMode[] = ['rgb', 'hex']
  const COLOR_PICKER_SWATCHES = ['#000000', '#FFFFFF', '#00bc7d', '#ff6900', '#ec003f', '#6e01a2', '#dcb4ff']
</script>

<template>
  <div
    class="border-border bg-surface-bg flex flex-wrap items-center gap-3 border-b px-5 py-2"
    role="toolbar"
    :aria-label="$t(toolbarAriaLabelKey)"
  >
    <!-- Группа: заголовок -->
    <NDropdown
      :options="headingOptions"
      trigger="click"
      :show-arrow="false"
      @select="handleHeadingSelect"
    >
      <button
        type="button"
        :class="getToolbarButtonClass(isHeadingActive)"
        :aria-label="$t('editor_toolbar_heading')"
        :aria-pressed="isHeadingActive"
        aria-haspopup="menu"
        tabindex="0"
      >
        <i
          class="fa-regular fa-heading text-xs"
          aria-hidden="true"
        />
      </button>
    </NDropdown>

    <span
      class="bg-border h-6 w-px shrink-0"
      aria-hidden="true"
    />

    <!-- Группа: размер шрифта -->
    <NPopselect
      :value="currentFontSize"
      :options="fontSizeOptions"
      trigger="click"
      @update:value="handleFontSizeUpdate"
    >
      <button
        type="button"
        :class="[fontSizeButtonClass, isFontSizeActive && toolbarButtonActiveClass]"
        :aria-label="$t('editor_toolbar_font_size')"
        :aria-pressed="isFontSizeActive"
        aria-haspopup="listbox"
        tabindex="0"
      >
        {{ currentFontSize ?? '—' }}
      </button>
    </NPopselect>

    <span
      class="bg-border h-6 w-px shrink-0"
      aria-hidden="true"
    />

    <!-- Группа: цвет + форматирование -->
    <NColorPicker
      :value="textColor"
      :modes="COLOR_PICKER_MODES"
      :swatches="COLOR_PICKER_SWATCHES"
      :show-alpha="false"
      @update:value="handleColorUpdate"
      @update:show="handleColorPickerShowUpdate"
    >
      <template #trigger="{ value, onClick, ref: triggerRef }">
        <button
          :ref="triggerRef"
          type="button"
          class="relative"
          :class="getToolbarButtonClass(isTextColorActive)"
          :aria-label="$t('editor_toolbar_text_color')"
          :aria-pressed="isTextColorActive"
          aria-haspopup="dialog"
          tabindex="0"
          @click="onClick"
        >
          <i
            class="fa-regular fa-palette text-xs"
            aria-hidden="true"
          />
          <span
            class="absolute bottom-0 mx-auto h-0.5 w-4/5"
            :style="{ backgroundColor: value || 'var(--color-body)' }"
          />
        </button>
      </template>
    </NColorPicker>

    <button
      v-for="action in formatActions"
      :key="action.icon"
      type="button"
      :class="getToolbarButtonClass(action.isActive)"
      :aria-label="$t(action.label)"
      :aria-pressed="action.isActive"
      tabindex="0"
      @click="action.run()"
      @keydown.enter.space.prevent="action.run()"
    >
      <i
        :class="['fa-regular', action.icon, 'text-xs']"
        aria-hidden="true"
      />
    </button>

    <span
      class="bg-border h-6 w-px shrink-0"
      aria-hidden="true"
    />

    <!-- Группа: выравнивание -->
    <button
      v-for="action in alignActions"
      :key="action.icon"
      type="button"
      :class="getToolbarButtonClass(action.isActive)"
      :aria-label="$t(action.label)"
      :aria-pressed="action.isActive"
      tabindex="0"
      @click="action.run()"
      @keydown.enter.space.prevent="action.run()"
    >
      <i
        :class="['fa-regular', action.icon, 'text-xs']"
        aria-hidden="true"
      />
    </button>

    <span
      class="bg-border h-6 w-px shrink-0"
      aria-hidden="true"
    />

    <!-- Группа: история -->
    <button
      v-for="action in historyActions"
      :key="action.icon"
      type="button"
      :class="toolbarButtonClass"
      :aria-label="$t(action.label)"
      :disabled="action.disabled"
      tabindex="0"
      @click="action.run()"
      @keydown.enter.space.prevent="action.run()"
    >
      <i
        :class="['fa-regular', action.icon, 'text-xs']"
        aria-hidden="true"
      />
    </button>

    <span
      class="ms-auto text-xs"
      :class="isCounterOverLimit ? 'text-error' : 'text-body'"
      aria-live="polite"
    >
      {{ contentCounterLabel }}
    </span>
  </div>
</template>
